import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
	Card,
	CardContent,
	Typography,
	Box,
	Tabs,
	Tab
} from '@material-ui/core';
import {
	Map as MapIcon,
	List as ListIcon
} from '@material-ui/icons';
import WorldMapVisualization from './WorldMapVisualization';
import ClusterOverviewTable from './ClusterOverviewTable';
import { getThemeColors } from '../utils/themeUtils';

const useStyles = makeStyles(theme => ({
	card: {
		backgroundColor: '#1e293b',
		borderRadius: '8px',
		border: '1px solid #334155',
		boxShadow: 'none',
		display: 'flex',
		flexDirection: 'column',
		height: '100%'
	},
	cardContent: {
		padding: theme.spacing(3),
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		'&:last-child': {
			paddingBottom: theme.spacing(3)
		}
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: theme.spacing(1.5),
		height: '7px'
	},
	title: {
		fontSize: '16px',
		fontWeight: 600,
		color: '#f8fafc'
	},
	tabs: {
		minHeight: '32px',
		'& .MuiTabs-indicator': {
			backgroundColor: '#1C75D2',
			height: '2px'
		}
	},
	tab: {
		minHeight: '32px',
		minWidth: 'auto',
		padding: '6px 12px',
		textTransform: 'none',
		fontSize: '13px',
		fontWeight: 500,
		color: '#94a3b8',
		'&.Mui-selected': {
			color: '#1C75D2'
		},
		'& .MuiTab-wrapper': {
			flexDirection: 'row',
			gap: '6px'
		}
	},
	tabIcon: {
		fontSize: '16px',
		marginBottom: '0 !important'
	},
	contentContainer: {
		flex: 1,
		marginTop: theme.spacing(1),
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
		maxHeight: '450px'
	},
	mapWrapper: {
		width: '100%',
		height: '100%',
		position: 'relative',
		backgroundColor: '#0f172a',
		borderRadius: '8px',
		overflow: 'hidden',
		border: '1px solid #1e293b'
	},
	tableWrapper: {
		width: '100%',
		height: '100%',
		maxHeight: '100%',
		overflowY: 'auto',
		overflowX: 'hidden',
		'&::-webkit-scrollbar': {
			width: '8px'
		},
		'&::-webkit-scrollbar-track': {
			backgroundColor: '#0f172a',
			borderRadius: '4px'
		},
		'&::-webkit-scrollbar-thumb': {
			backgroundColor: '#334155',
			borderRadius: '4px',
			'&:hover': {
				backgroundColor: '#475569'
			}
		}
	},
	tableInner: {
		// Remove card styling from inner table
		'& > div': {
			backgroundColor: 'transparent',
			border: 'none',
			boxShadow: 'none',
			borderRadius: 0
		},
		'& .MuiCardContent-root': {
			padding: 0
		}
	}
}));

const ClusterVisualization = ({ clusters, themeMode = 'dark', userView = false, user = null }) => {
	const classes = useStyles();
	const colors = getThemeColors(themeMode);
	const [activeTab, setActiveTab] = useState(0);
	
	// Check if user has 'demo' role (check both 'role' and 'roles' fields)
	const isDemoUser = user && (
		(user.role && Array.isArray(user.role) && user.role.some(r => 
			typeof r === 'string' && r.toLowerCase().includes('demo')
		)) ||
		(user.roles && Array.isArray(user.roles) && user.roles.some(r => 
			typeof r === 'string' && r.toLowerCase().includes('demo')
		))
	);
	
	// Set title based on demo user status
	const mapTitle = isDemoUser ? 'Heidi AI Supercomputers' : 'Global Cluster Distribution';
	
	// Transform all clusters to show as green/available for user dashboard
	const displayClusters = userView ? clusters.map(cluster => ({
		...cluster,
		status: 'available',
		color: '#22c55e'
	})) : clusters;

	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
	};

	return (
		<Card 
			className={classes.card}
			style={{
				backgroundColor: colors.card.background,
				border: `1px solid ${colors.card.border}`,
				boxShadow: colors.card.shadow
			}}
		>
			<CardContent className={classes.cardContent}>
			<Box className={classes.header}>
				<Typography className={classes.title} style={{ color: colors.text.primary }}>
					{activeTab === 0 ? mapTitle : 'Clusters'}
				</Typography>
					<Tabs 
						value={activeTab} 
						onChange={handleTabChange}
						className={classes.tabs}
					>
						<Tab 
							label="Map" 
							icon={<MapIcon className={classes.tabIcon} style={{ color: activeTab === 0 ? colors.accent.blue : colors.text.secondary }} />}
							className={classes.tab}
							style={{ color: activeTab === 0 ? colors.accent.blue : colors.text.secondary }}
						/>
						{!userView && (
							<Tab 
								label="List" 
								icon={<ListIcon className={classes.tabIcon} style={{ color: activeTab === 1 ? colors.accent.blue : colors.text.secondary }} />}
								className={classes.tab}
								style={{ color: activeTab === 1 ? colors.accent.blue : colors.text.secondary }}
							/>
						)}
					</Tabs>
				</Box>

				<div 
					className={classes.contentContainer}
					style={{ maxHeight: userView ? 'none' : '450px' }}
				>
					{activeTab === 0 ? (
						<div className={classes.mapWrapper} style={{ backgroundColor: colors.map.background, border: `1px solid ${colors.border.primary}` }}>
							<WorldMapVisualization clusters={displayClusters} embedded themeMode={themeMode} userView={userView} />
						</div>
					) : (
						<div className={classes.tableWrapper}>
							<div className={classes.tableInner}>
								<ClusterOverviewTable clusters={displayClusters} embedded themeMode={themeMode} />
							</div>
						</div>
					)}
				</div>
			</CardContent>
		</Card>
	);
};

// Memoize component to prevent unnecessary re-renders
// Only re-render if clusters data actually changes (deep comparison)
const arePropsEqual = (prevProps, nextProps) => {
	// Compare theme mode
	if (prevProps.themeMode !== nextProps.themeMode) {
		return false;
	}
	
	// Compare clusters array length
	if (prevProps.clusters?.length !== nextProps.clusters?.length) {
		return false;
	}
	
	// If both are empty/null, they're equal
	if (!prevProps.clusters && !nextProps.clusters) {
		return true;
	}
	
	// Deep comparison of cluster data (id, status, metrics)
	// This prevents re-render when clusters array reference changes but content is same
	if (prevProps.clusters && nextProps.clusters) {
		for (let i = 0; i < prevProps.clusters.length; i++) {
			const prev = prevProps.clusters[i];
			const next = nextProps.clusters[i];
			
			// Compare critical fields that would need UI update
			if (prev.id !== next.id || 
				prev.status !== next.status ||
				prev.name !== next.name ||
				JSON.stringify(prev.location) !== JSON.stringify(next.location)) {
				return false;
			}
		}
	}
	
	return true;
};

export default React.memo(ClusterVisualization, arePropsEqual);
